import React, { useState } from 'react';

import { defineMessages } from 'react-intl';

import { useAssets } from '~/techstyle-shared/react-assets';
import { useBreakpoint } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import {
  ASSET_CONTAINER_MOBILE,
  ASSET_CONTAINER_DESKTOP,
  ModalContainer,
  BackgroundImageWrapper,
  BackgroundFallback,
  BackgroundImage,
  BackgroundGradient,
  ContentWrapper,
  HeaderText,
  SubText,
  CTAButton,
  LegalSection,
  LegalCopyOutlineRb,
  LegalCopyOutlineText,
  LegalBodyText,
  LegalCopySeeMoreCollapsable,
  LegalCopySeeMoreContent,
  LegalCopyTitle,
} from './SunbitModalContent.styles';

const messages = defineMessages({
  title: {
    id: 'branded_credit_card_modal.title_preapproved',
    defaultMessage: 'Good News, {firstName}!',
  },
  subtext: {
    id: 'branded_credit_card_modal.subtext_preapproved',
    defaultMessage:
      'You are Pre-Approved for the Fab Credit Card offer up to {offerAmount}.',
  },
  cta: {
    id: 'branded_credit_card_modal.cta_preapproved',
    defaultMessage: 'Apply',
  },
  legal: {
    id: 'branded_credit_card_modal.legal_preapproved',
    defaultMessage:
      '"Terms and restrictions apply. The Fabletics credit card is issued by Transportation Alliance Bank Inc., dba TAB Bank, pursuant to a license from Visa U.S.A. Inc. Click for more details."',
  },
});

const legalCopyMessages = defineMessages({
  legalCopyIssuerDisclosure: {
    id: 'branded_credit_card_modal.preapproved.legal_copy_issuer_disclosure',
    defaultMessage:
      'The Fabletics Reserve Card is issued by Transportation Alliance Bank Inc. dba TAB Bank, pursuant to a license from Visa U.S.A. Inc. See {importantDisclosuresLink} for rates and restrictions.',
  },
  legalCopySeeMoreCollapsable: {
    id: 'branded_credit_card_modal.preapproved.legal_copy_see_more_collapsable',
    defaultMessage:
      'PRESCREEN & OPT-OUT NOTICE and TERMS & CONDITIONS OF OFFER - Click to open/Scroll down',
  },
  legalCopyOptOutNoticeTitle: {
    id: 'branded_credit_card_modal.preapproved.legal_copy_opt_out_notice_title',
    defaultMessage: 'PRESCREEN & OPT-OUT NOTICE:',
  },
  legalCopyOptOutNoticeBody: {
    id: 'branded_credit_card_modal.preapproved.legal_copy_opt_out_notice_body',
    defaultMessage:
      'This "prescreened" offer of credit is based on information in your credit report indicating that you meet certain criteria. This offer is not guaranteed if you do not meet our criteria. If you do not want to receive prescreened offers of credit from this and other companies, contact TransUnion Opt Out Request, P.O. Box 505, Woodlyn, PA 19094-0505; Equifax Information Services LLC, P.O. Box 740123, Atlanta, GA 30374-0123; Experian Consumer Opt Out, P.O. Box 703, Allen, TX 75013 or call 1-888-567-8688.',
  },
  legalCopyTermsTitle: {
    id: 'branded_credit_card_modal.preapproved.legal_copy_terms_title',
    defaultMessage: 'TERMS AND CONDITIONS OF OFFER:',
  },
  legalCopyTermsBody: {
    id: 'branded_credit_card_modal.preapproved.legal_copy_terms_body',
    defaultMessage:
      'This offer is contingent on the following: Your submission of an application for financing, including income and housing information, no later than the response date stated in the offer. Sunbit must be able to confirm your identity and application information. You must continue to meet the consumer report criteria used to select you for this offer and the underwriting criteria. You must be at least 18 years of age. You must not have a delinquent loan through or with us. You must not already have a Sunbit Card. You may also be required to submit bank account or debit card details. You must agree to receive the card agreement and all other account disclosures provided with your card issuance in an electronic format and provide your signature electronically.',
  },
});

interface PreApprovedModalContentProps {
  firstName?: string;
  offerAmount?: string;
  onCTAClick?: () => void;
}

export default function PreApprovedModalContent({
  firstName,
  offerAmount,
  onCTAClick,
}: PreApprovedModalContentProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { isMobile } = useBreakpoint();

  const assetsData = useAssets([
    ASSET_CONTAINER_MOBILE,
    ASSET_CONTAINER_DESKTOP,
  ]);

  const mobileAsset = assetsData?.find(
    (asset) => asset.name === ASSET_CONTAINER_MOBILE
  );
  const desktopAsset = assetsData?.find(
    (asset) => asset.name === ASSET_CONTAINER_DESKTOP
  );

  const assetData = isMobile ? mobileAsset?.data : desktopAsset?.data;
  const asset = assetData?.assets?.[0];

  const handleCTAClick = () => {
    if (onCTAClick) {
      onCTAClick();
    }
  };

  const renderBackground = () => {
    if (!asset) {
      return <BackgroundFallback />;
    }
    return <BackgroundImage asset={asset} />;
  };

  return (
    <ModalContainer>
      <BackgroundImageWrapper>
        {renderBackground()}
        <BackgroundGradient />
      </BackgroundImageWrapper>

      <ContentWrapper>
        <HeaderText>
          <FormattedMessage
            {...messages.title}
            values={{ firstName: firstName || '' }}
          />
        </HeaderText>
        <SubText>
          <FormattedMessage
            {...messages.subtext}
            values={{ offerAmount: offerAmount ? `$${offerAmount}` : '' }}
          />
        </SubText>
        <CTAButton onClick={handleCTAClick} data-autotag="SunbitCTAButton">
          <FormattedMessage {...messages.cta} />
        </CTAButton>
        <LegalSection>
          {/* Outline box with legal copy */}
          <LegalCopyOutlineRb>
            <LegalCopyOutlineText>
              <FormattedMessage {...messages.legal} />
            </LegalCopyOutlineText>
          </LegalCopyOutlineRb>
          {/* Issuer disclosure */}
          <LegalBodyText>
            <FormattedMessage
              {...legalCopyMessages.legalCopyIssuerDisclosure}
            />
          </LegalBodyText>
          {/* See more collapsable — expands full legal copy */}
          <LegalCopySeeMoreCollapsable
            onClick={() => setIsDrawerOpen((prev) => !prev)}
            aria-expanded={isDrawerOpen}
          >
            <FormattedMessage
              {...legalCopyMessages.legalCopySeeMoreCollapsable}
            />
          </LegalCopySeeMoreCollapsable>
          <LegalCopySeeMoreContent $isOpen={isDrawerOpen}>
            <LegalCopyTitle>
              <FormattedMessage
                {...legalCopyMessages.legalCopyOptOutNoticeTitle}
              />
            </LegalCopyTitle>
            <LegalBodyText>
              <FormattedMessage
                {...legalCopyMessages.legalCopyOptOutNoticeBody}
              />
            </LegalBodyText>
            <LegalCopyTitle>
              <FormattedMessage {...legalCopyMessages.legalCopyTermsTitle} />
            </LegalCopyTitle>
            <LegalBodyText>
              <FormattedMessage {...legalCopyMessages.legalCopyTermsBody} />
            </LegalBodyText>
          </LegalCopySeeMoreContent>
        </LegalSection>
      </ContentWrapper>
    </ModalContainer>
  );
}
